<template>
    <article class="asset-tile" :data-mechanic-asset-id="unit.id">
        <div class="asset-tile__media" :aria-busy="loadingPhotos ? 'true' : 'false'">
            <button type="button" class="asset-tile__photo" :aria-label="`Открыть карточку: ${title}`"
                    @click="$emit('open', unit.id)">
                <img v-if="currentPhoto && !failedPhotos[currentPhoto.id]" :key="currentPhoto.id"
                     :src="photoSrc" :alt="title" loading="lazy" decoding="async"
                     @error="$set(failedPhotos, currentPhoto.id, true)" />
                <i v-else class="fas fa-camera" aria-hidden="true"></i>
            </button>
            <b-badge :variant="statusVariant" class="asset-tile__status">
                {{ unit.status_label || unit.status }}
            </b-badge>
            <button v-if="canCopy" type="button" class="asset-tile__copy"
                    title="Копировать позицию" aria-label="Копировать позицию" @click="$emit('copy', unit)">
                <i class="fas fa-copy" aria-hidden="true"></i>
            </button>
            <template v-if="canBrowsePhotos">
                <button type="button" class="asset-tile__arrow asset-tile__arrow--previous"
                        aria-label="Предыдущее фото" :disabled="loadingPhotos" @click="changePhoto(-1)">
                    <i class="fas fa-chevron-left" aria-hidden="true"></i>
                </button>
                <button type="button" class="asset-tile__arrow asset-tile__arrow--next"
                        aria-label="Следующее фото" :disabled="loadingPhotos" @click="changePhoto(1)">
                    <i class="fas fa-chevron-right" aria-hidden="true"></i>
                </button>
            </template>
            <span v-if="loadingPhotos" class="asset-tile__counter" role="status">Загрузка…</span>
            <button v-else-if="photoError" type="button" class="asset-tile__retry" @click="changePhoto(1)">
                Не удалось загрузить фото · Повторить
            </button>
            <span v-else-if="photos && photos.length > 1" class="asset-tile__counter" aria-live="polite">
                {{ photoIndex + 1 }} / {{ photos.length }}
            </span>
            <span v-if="quantity" class="asset-tile__quantity">{{ quantity }}</span>
        </div>
        <div class="asset-tile__caption">
            <a href="" class="asset-tile__title" :title="title" @click.prevent="$emit('open', unit.id)">{{ title }}</a>
            <div class="asset-tile__identifier" :title="identifier">{{ identifier }}</div>
            <div class="asset-tile__location" :title="location || 'Место не указано'">
                <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
                {{ location || 'Место не указано' }}
            </div>
        </div>
    </article>
</template>

<script>
import ajax from '@/utils/ajax';
import {isImageFile} from '@/utils/fileViewer';

export default {
    name: `MechanicAssetTile`,
    props: {
        unit: {type: Object, required: true},
        title: {type: String, required: true},
        location: {type: String, default: ``},
        quantity: {type: String, default: ``},
        statusVariant: {type: String, default: `secondary`},
        canCopy: {type: Boolean, default: false},
        fileUrl: {type: Function, required: true}
    },
    data() {
        return {photos: null, photoIndex: 0, loadingPhotos: false, photoError: false, failedPhotos: {}, photoRequest: 0};
    },
    computed: {
        identifier() {
            const isMotorTransport = String(this.unit.catalog_name || ``).trim().toLowerCase() === `автотранспорт`;
            const plateNumber = String(this.unit.plate_number || ``).trim();
            if (isMotorTransport && plateNumber) return `Гос. ${plateNumber}`;
            const candidates = [
                [`Инв.`, this.unit.inventory_number],
                [`QR`, this.unit.qr_code],
                [`Гос.`, this.unit.plate_number],
                [`Сер.`, this.unit.serial_number],
                [`VIN`, this.unit.vin]
            ];
            for (const [label, value] of candidates) {
                const number = value === null || value === undefined ? `` : String(value).trim();
                if (number) return `${label} ${number}`;
            }
            return `ID ${this.unit.id}`;
        },
        currentPhoto() {
            return this.photos ? this.photos[this.photoIndex] || null : this.unit.primary_photo || null;
        },
        photoSrc() {
            return this.currentPhoto ? `${this.fileUrl(this.unit, this.currentPhoto, 640, 480)}&fit=inside` : ``;
        },
        canBrowsePhotos() {
            return this.photos ? this.photos.length > 1 : Boolean(this.unit.primary_photo);
        }
    },
    watch: {
        unit() {
            this.photoRequest++;
            this.photos = null;
            this.photoIndex = 0;
            this.loadingPhotos = false;
            this.photoError = false;
            this.failedPhotos = {};
        }
    },
    methods: {
        async changePhoto(direction) {
            if (this.loadingPhotos) return;
            this.photoError = false;
            if (!this.photos) {
                this.loadingPhotos = true;
                const request = ++this.photoRequest;
                try {
                    // Fetch metadata only on the first arrow click, never for every tile on mount.
                    const details = await ajax.getMechanicAssetUnit(this.unit.id);
                    if (this._isDestroyed || request !== this.photoRequest) return;
                    const primaryId = this.unit.primary_photo && this.unit.primary_photo.id;
                    const seen = new Set();
                    this.photos = (Array.isArray(details.Files) ? details.Files : []).filter(file => {
                        if (!file || !file.id || !isImageFile(file) || seen.has(String(file.id))) return false;
                        seen.add(String(file.id));
                        return true;
                    }).sort((a, b) => {
                        const aPrimary = String(a.id) === String(primaryId);
                        const bPrimary = String(b.id) === String(primaryId);
                        return aPrimary !== bPrimary ? (aPrimary ? -1 : 1) : Number(b.id) - Number(a.id);
                    });
                } catch (error) {
                    if (!this._isDestroyed && request === this.photoRequest) this.photoError = true;
                    return;
                } finally {
                    if (!this._isDestroyed && request === this.photoRequest) this.loadingPhotos = false;
                }
            }
            if (this.photos.length > 1) {
                this.photoIndex = (this.photoIndex + direction + this.photos.length) % this.photos.length;
            }
        }
    }
};
</script>

<style scoped>
.asset-tile {
    min-width: 0;
    overflow: hidden;
    border: 1px solid #bdc9d7;
    border-radius: 9px;
    background: #fff;
    box-shadow: 0 2px 6px rgba(35, 55, 80, 0.1);
    transition: border-color 140ms ease, box-shadow 140ms ease;
}

.asset-tile:focus-within {
    border-color: #2680d9;
    box-shadow: 0 0 0 2px rgba(38, 128, 217, 0.3), 0 4px 12px rgba(35, 55, 80, 0.14);
}

@media (hover: hover) {
    .asset-tile:hover {
        border-color: #2680d9;
        box-shadow: 0 0 0 2px rgba(38, 128, 217, 0.3), 0 4px 12px rgba(35, 55, 80, 0.14);
    }
}

@media (prefers-reduced-motion: reduce) {
    .asset-tile { transition: none; }
}

.asset-tile__media {
    position: relative;
    aspect-ratio: 3 / 2;
    background: #edf1f6;
    border-bottom: 1px solid #d5deea;
}

.asset-tile__photo {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
    padding: 6px;
    border: 0;
    background: transparent;
    color: #adb5bd;
    font-size: 30px;
}

.asset-tile__photo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.asset-tile__status {
    position: absolute;
    top: 7px;
    left: 7px;
    max-width: calc(100% - 48px);
    white-space: normal;
    pointer-events: none;
}

.asset-tile__copy, .asset-tile__arrow {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 1px solid #d5dbe1;
    border-radius: 5px;
    color: #495057;
    background: rgba(255, 255, 255, 0.92);
}

.asset-tile__copy { top: 5px; right: 5px; width: 26px; height: 26px; font-size: 12px; }
.asset-tile__arrow { top: 50%; transform: translateY(-50%); width: 30px; height: 34px; }
.asset-tile__arrow--previous { left: 5px; }
.asset-tile__arrow--next { right: 5px; }
.asset-tile__arrow:disabled { opacity: 0.5; }
.asset-tile__copy:hover, .asset-tile__arrow:hover { background: #e9ecef; }

.asset-tile__counter, .asset-tile__quantity, .asset-tile__retry {
    position: absolute;
    bottom: 6px;
    padding: 2px 6px;
    border: 0;
    border-radius: 4px;
    background: rgba(33, 37, 41, 0.8);
    color: #fff;
    font-size: 11px;
    line-height: 1.4;
}
.asset-tile__counter { left: 6px; pointer-events: none; }
.asset-tile__quantity { right: 6px; max-width: 50%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; pointer-events: none; }
.asset-tile__retry { left: 6px; right: 6px; z-index: 1; }
.asset-tile__caption { padding: 8px 10px; }
.asset-tile__title {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    overflow-wrap: anywhere;
    height: 36px;
    font-size: 14px;
    line-height: 18px;
    font-weight: 600;
    color: #212529;
}
.asset-tile__location {
    margin-top: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    line-height: 18px;
    color: #6c757d;
}
.asset-tile__identifier {
    margin-top: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    line-height: 16px;
    font-weight: 600;
    color: #49576a;
}
.asset-tile__location i { margin-right: 3px; }
.asset-tile button:focus-visible, .asset-tile a:focus-visible { outline: 2px solid #007bff; outline-offset: -2px; }
</style>
